"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useSwiperEarlyDestroy } from "@/hooks/use-swiper-early-destroy";

const comparisonCards = [
  {
    title: "Support Beyond Setup",
    description:
      "Company formation is only the beginning. We help you stay on top of the work that comes after registration.",
    image: "/home-v3/auto-added-frame.svg",
    imageAlt: "Auto added frame",
    imageClass: "absolute -bottom-10 -left-20 h-[227px] w-[312px]",
    titleClass: "text-2xl sm:whitespace-nowrap",
    // Innermost shine layer's fill-opacity — was previously the alpha byte of
    // a Tailwind bg-[#ffffffXX] class (ff = 1, b2 = 0.7, cc = 0.8).
    glowOpacity: 1,
  },
  {
    title: "Clean Records, Less Stress",
    description:
      "We keep your records organized, so tax filing, reporting, and decisions feel less stressful.",
    image: "/home-v3/image-27.png",
    imageAlt: "Image",
    imageClass: "absolute -bottom-12 -left-10 h-[254px] w-[270px]",
    titleClass: "text-2xl sm:whitespace-nowrap",
    glowOpacity: 0.7,
  },
  {
    title: "Compliance With Clarity",
    description:
      "You will not have to guess what is due, missing, or next. We help keep important obligations on track.",
    image: "/home-v3/image-28.png",
    imageAlt: "Image",
    imageClass: "absolute -bottom-12 -left-10 h-[291.79px] w-[324.69px]",
    titleClass: "text-xl",
    glowOpacity: 0.8,
  },
  {
    title: "Structure That Scales",
    description:
      "As your business changes, we help keep the financial and compliance side easier to manage.",
    image: "/home-v3/image-29.png",
    imageAlt: "Image",
    imageClass: "absolute -bottom-10 -left-10 h-[241px] w-[293.94px]",
    titleClass: "text-xl sm:whitespace-nowrap",
    glowOpacity: 1,
  },
];

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// The card shine, top-right: an outer blurred circle, a paler blurred
// ellipse, and an inner white ellipse (opacity varies per card). Grain is
// layered over the composited result of all three via a filter on the
// wrapping <g> — the same two-level nesting (individual blur per shape,
// shared grain over the group) as the CSS version this replaced, just as
// real SVG shapes with `filter="url(#...)"` attributes instead of a CSS
// `filter: url(#...)` reference on a plain <div>. That distinction matters:
// applying an SVG filter directly to an SVG shape is the well-supported,
// spec-primary path (this is how Shine.svg and home.svg already do it,
// with no rendering issues); applying one via CSS to an HTML element is an
// edge case with a real Chromium bug where the filter isn't computed on
// first paint, only once something else forces a repaint — which is why
// the noise previously only appeared after dragging the Swiper. Two
// different attempts to patch that CSS-based version (a `userSpaceOnUse`
// filter region, then `will-change: filter`) each broke something else
// instead of fixing it, which is what settled on rebuilding it this way.
const comparisonShineFilterIds = {
  outerBlur: "comparison-shine-blur-outer",
  midBlur: "comparison-shine-blur-mid",
  innerBlur: "comparison-shine-blur-inner",
  grain: "comparison-shine-grain",
};

const ComparisonCardShineDefs = (): JSX.Element => (
  <svg width="0" height="0" className="absolute" aria-hidden="true">
    <defs>
      <filter
        id={comparisonShineFilterIds.outerBlur}
        x="-150%"
        y="-150%"
        width="400%"
        height="400%"
      >
        <feGaussianBlur stdDeviation="71.73" />
      </filter>
      <filter
        id={comparisonShineFilterIds.midBlur}
        x="-150%"
        y="-150%"
        width="400%"
        height="400%"
      >
        <feGaussianBlur stdDeviation="71.73" />
      </filter>
      <filter
        id={comparisonShineFilterIds.innerBlur}
        x="-100%"
        y="-100%"
        width="300%"
        height="300%"
      >
        <feGaussianBlur stdDeviation="37.02" />
      </filter>
      <filter
        id={comparisonShineFilterIds.grain}
        x="-100%"
        y="-100%"
        width="300%"
        height="300%"
        colorInterpolationFilters="sRGB"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="2 2"
          stitchTiles="stitch"
          numOctaves="3"
          result="noise"
          seed="1528"
        />
        <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
        <feComponentTransfer in="alphaNoise" result="thresholdedNoise">
          <feFuncA
            type="discrete"
            tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
          />
        </feComponentTransfer>
        <feComposite operator="in" in="thresholdedNoise" in2="SourceGraphic" result="noiseClippedToShine" />
        <feFlood floodColor="#FFFFFF" floodOpacity="0.28" result="whiteFlood" />
        <feComposite operator="in" in="whiteFlood" in2="noiseClippedToShine" result="tintedNoise" />
        <feMerge>
          <feMergeNode in="SourceGraphic" />
          <feMergeNode in="tintedNoise" />
        </feMerge>
      </filter>
    </defs>
  </svg>
);

const ComparisonCardShine = ({ glowOpacity }: { glowOpacity: number }): JSX.Element => (
  <svg
    aria-hidden="true"
    className="pointer-events-none absolute -right-[123px] -top-[19px] h-[171px] w-[171px] overflow-visible"
    viewBox="0 0 171 171"
    fill="none"
  >
    <g filter={`url(#${comparisonShineFilterIds.grain})`}>
      <g filter={`url(#${comparisonShineFilterIds.outerBlur})`}>
        <circle cx="85.5" cy="85.5" r="85.5" fill="#6FA6FF" />
      </g>
      <g filter={`url(#${comparisonShineFilterIds.midBlur})`}>
        <ellipse cx="85.5" cy="52.5" rx="85.5" ry="52.5" fill="#D2E3FF" />
      </g>
      <g filter={`url(#${comparisonShineFilterIds.innerBlur})`}>
        <ellipse cx="85.5" cy="44.5" rx="85.5" ry="44.5" fill="white" fillOpacity={glowOpacity} />
      </g>
    </g>
  </svg>
);

export const ProviderComparisonIntroSection = (): JSX.Element => {
  const { onSwiper } = useSwiperEarlyDestroy();
  return (
    <section className="relative mt-20 sm:mt-32 flex w-full flex-col items-center gap-14 px-2 pb-5  lg:px-[108px]">
      <ComparisonCardShineDefs />
      <header className="flex w-full max-w-[1224px] flex-col items-center gap-4 text-center">
        <Badge className="animate-revolving-border h-auto rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100 [-webkit-backdrop-filter:blur(3.5px)_brightness(100%)]">
          Protection and structure, not just paperwork
        </Badge>
        <motion.h2
          className="max-w-[1008px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-4xl font-normal leading-tight tracking-[0] text-white sm:text-5xl sm:leading-[64px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Most providers help with forms. We
          <br />
          help with what comes after too.
        </motion.h2>
      </header>
      <motion.div
        className="relative z-0 w-full max-w-[1640px]"
        variants={cardsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <Swiper
          onSwiper={onSwiper}
          slidesPerView="auto"
          spaceBetween={24}
          className="!px-1 [&_.swiper-wrapper]:!items-center"
        >
          {comparisonCards.map((card) => (
            <SwiperSlide
              key={card.title}
              className="!w-[calc(100vw-3.5rem)] max-w-[392px]"
            >
              <motion.div variants={cardVariants}>
                <Card className="relative h-[479px] w-full shrink-0 overflow-hidden rounded-[32px] border-0 bg-[#0f1218] p-8 text-white shadow-none sm:p-10">
                  <div
                    aria-hidden="true"
                    className="absolute left-[-119px] top-[334px] h-72 w-[605px] rounded-[302.5px/144px] bg-[#0061fe] blur-[137.02px]"
                  />
                  <ComparisonCardShine glowOpacity={card.glowOpacity} />
                  <CardContent className="relative z-10 flex h-full p-0">
                    <div className="flex flex-col items-start gap-4">
                      <h3
                        className={`mt-[-1px] [font-family:'Suisse_Intl_Trial-Bold',Helvetica] font-bold leading-7 tracking-[0] text-white ${card.titleClass}`}
                      >
                        {card.title}
                      </h3>
                      <p className="w-full max-w-[312px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-6 tracking-[0] text-white">
                        {card.description}
                      </p>
                    </div>
                    <img
                      className={card.imageClass}
                      alt={card.imageAlt}
                      src={card.image}
                    />
                  </CardContent>
                </Card>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
        <motion.div
          variants={cardVariants}
          className="pointer-events-none max-sm:hidden absolute -left-24 top-[380px] z-10 h-[199px] w-[180px]"
        >
          <img
            className="h-full w-full animate-float-reverse object-cover"
            alt="Image"
            src="/home-v3/image-38-1.png"
          />
        </motion.div>
      </motion.div>
   
    </section>
  );
};
